import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  COLORS,
  PLACE_YOUR_BETS,
  NO_MORE_BETS,
  LAST_BETS,
  DISCONNECTED,
  RECONNECTING,
  TABLE_CLOSED,
  INSURANCE_CALLS,
  CANCEL_ROUND,
} from '@ezugi/constants';
import { RoundStatus } from '@ezugi/types';
import { formatTimeToMinutes } from '@ezugi/utils';

import { roundStatusSelector } from '../store/selectors';
import { useGameTimer, GameTimerProps } from './useGameTimer';

const defaultStateColors: Record<string, string> = {
  [PLACE_YOUR_BETS]: COLORS.GREEN || '#b4de85',
  [INSURANCE_CALLS]: COLORS.GREEN || '#b4de85',
  [LAST_BETS]: COLORS.ORANGE || '#ffc86e',
  [DISCONNECTED]: COLORS.RED || '#ff252f',
  [RECONNECTING]: COLORS.RED || '#ff252f',
  [TABLE_CLOSED]: COLORS.RED || '#ff252f',
  LOW_BALANCE: COLORS.RED || '#ff252f',
};

type UseGameProgressProps = GameTimerProps & {
  bettingStates?: RoundStatus[],
  stateColors?: Record<string, string>,
};

export function useGameProgress({
  bettingStates = [ PLACE_YOUR_BETS, LAST_BETS ],
  startStates = [ PLACE_YOUR_BETS ],
  stopStates = [ NO_MORE_BETS, CANCEL_ROUND ],
  stateColors = defaultStateColors,
}: UseGameProgressProps = {}) {
  const roundStatus = useSelector(roundStatusSelector);
  const { timeLeft = 0, totalTime = 0 } = useGameTimer({
    startStates,
    stopStates,
  });

  const state = [ PLACE_YOUR_BETS, LAST_BETS ].includes(roundStatus)
    && totalTime > 0
    && timeLeft <= 0
    ? NO_MORE_BETS
    : roundStatus;

  const timeString = useMemo(() => formatTimeToMinutes(timeLeft / 1000), [ timeLeft ]);

  const progress = (timeLeft || 0) / (totalTime || 0);

  return {
    state,
    color: defaultStateColors[state] || stateColors[state] || 'transparent',
    timeLeft: (timeLeft || 0) / 1000,
    totalTime: (totalTime || 0) / 1000,
    timeString,
    progress: Number.isNaN(progress) || progress === Infinity ? 0 : progress,
    areBetsOpen: bettingStates.includes(state),
  };
}
